import React, { memo, CSSProperties } from "react";
import { useTheme } from "@mui/material";

export interface StandardNodeProps {
  /** Optional React elements for node sections */
  header?: React.ReactNode;
  functionalities?: React.ReactNode;
  footer?: React.ReactNode;

  /** Style customization */
  width?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  backgroundColor?: string;

  /** React Flow selected state (for glow effect) */
  selected?: boolean;
}

/**
 * A fully flexible standard node container.
 * Renders any passed header, functionality, and footer components,
 * with standardized layout and theming.
 */
const StandardNode: React.FC<StandardNodeProps> = ({
  header,
  functionalities,
  footer,
  width = 300,
  borderRadius = 8,
  borderWidth = 3,
  borderColor,
  backgroundColor,
  selected = false,
}) => {
  const theme = useTheme();

  const nodeStyle: CSSProperties = {
    border: `${borderWidth}px solid ${borderColor || theme.custom.base.primary.dark}`,
    borderRadius: `${borderRadius}px`,
    width,
    overflow: "hidden",
    fontFamily: "Arial, sans-serif",
    position: "relative",
    backgroundColor: backgroundColor || theme.custom.nodes.background.main.dark,
    boxShadow: selected
      ? "0 0 0 4px rgba(0, 150, 255, 0.5)"
      : "0px 2px 6px rgba(0, 0, 0, 0.1)",
    transition: "box-shadow 0.2s ease-in-out",
  };

  return (
    <div style={nodeStyle}>
      {header && <div>{header}</div>}
      {functionalities && <div>{functionalities}</div>}
      {footer && <div>{footer}</div>}
    </div>
  );
};

export default memo(StandardNode);
